import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  CheckCircle2,
  Clock,
  Factory,
  Layers,
  Network,
  Shield,
  Settings,
  BarChart2,
  Activity,
  Box,
  Database,
} from "lucide-react";
import { useEnquiryModal } from "../context/EnquiryModalContext";
import { motion, useMotionValue, useTransform, animate } from "framer-motion";
import { SpotlightCard } from "../../components/ui/SpotlightCard";
import { Sparkles } from "../../components/ui/Sparkles";
import { HeroBranchingSystem } from "../../components/ui/HeroBranchingSystem";
import "./MaxHome.css";

const ZERO_PILLARS = [
  { label: "Zero defect", Icon: CheckCircle2 },
  { label: "Zero downtime", Icon: Clock },
  { label: "Zero accident", Icon: Shield },
];

const CORE_PILLARS = [
  {
    label: "Asset Management",
    Icon: Database,
    desc: "Digital lifecycle tracking of plants, machines, lines, and part inventories.",
  },
  {
    label: "Planned Maintenance",
    Icon: Settings,
    desc: "Automated PM scheduling, technician task management, and work order tracking.",
  },
  {
    label: "OEE Analytics",
    Icon: BarChart2,
    desc: "Real-time visibility into availability, performance, and quality metrics.",
  },
  {
    label: "Safety & Hazard",
    Icon: Shield,
    desc: "Incident reporting, safety behavior tracking, and continuous safety audits.",
  },
  {
    label: "Quality (RCA/CAPA)",
    Icon: Activity,
    desc: "Enterprise tools for Root Cause Analysis and corrective quality actions.",
  },
  {
    label: "Spare Parts Inventory",
    Icon: Box,
    desc: "Prevent workflow disruptions with automated stock tracking and analytics.",
  },
];

const StatCounter = ({ from, to }) => {
  const count = useMotionValue(from);
  const rounded = useTransform(count, (latest) => Math.round(latest));

  useEffect(() => {
    const controls = animate(count, to, { duration: 2, ease: "easeOut" });
    return controls.stop;
  }, [count, to]);

  return <motion.span>{rounded}</motion.span>;
};

const staggerContainer = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.15 },
  },
};

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  show: {
    opacity: 1,
    y: 0,
    transition: { type: "spring", stiffness: 100 },
  },
};

const MaxHome = () => {
  const { openEnquiryModal } = useEnquiryModal();

  const openEnquiry = (e) => {
    e.preventDefault();
    openEnquiryModal();
  };

  useEffect(() => {
    document.title = "MaX Consulting | Manufacturing consulting & SaaS";
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="max-home">
      {/* Moving SVG Background */}
      <div className="max-home__hero-bg">
        <svg
          className="max-home__wireframe"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
        >
          <motion.path
            className="max-home__wireframe-path"
            d="M0,50 Q25,25 50,50 T100,50 M0,70 Q25,95 50,70 T100,70 M0,30 Q25,5 50,30 T100,30 M50,0 L50,100 M25,0 L25,100 M75,0 L75,100"
            vectorEffect="non-scaling-stroke"
            animate={{ x: [-5, 5, -5], y: [-2, 2, -2] }}
            transition={{ ease: "easeInOut", duration: 15, repeat: Infinity }}
          />
        </svg>
      </div>

      <motion.section
        className="max-home__hero"
        aria-labelledby="max-hero-heading"
        variants={staggerContainer}
        initial="hidden"
        animate="show"
      >
        <div className="max-container max-home__hero-inner">
          <div className="max-home__hero-copy">
            <motion.div variants={fadeUp} className="max-home__live-indicator">
              <motion.span
                className="max-home__live-dot"
                animate={{ opacity: [0.3, 1, 0.3] }}
                transition={{ repeat: Infinity, duration: 2 }}
              />
              System Live
            </motion.div>
            <motion.p variants={fadeUp} className="max-home__eyebrow">
              Manufacturing · Operations · Digital
            </motion.p>
            <motion.h1
              variants={fadeUp}
              id="max-hero-heading"
              className="max-home__title"
            >
              <span style={{ display: "inline-block", whiteSpace: "nowrap" }}>
                Precision in Every Shift.
              </span>
              <span className="max-home__title-break" style={{ display: "inline-block", whiteSpace: "nowrap", marginTop: "0.1em" }}>
                Power in Every Asset.
              </span>
            </motion.h1>
            <motion.p variants={fadeUp} className="max-home__lead">
              MaX Consulting brings over <strong>30+ years</strong> of hands-on
              manufacturing experience — not just software, but end-to-end
              advisory, implementation, and a modern SaaS platform built for how
              plants actually run.
            </motion.p>
            <motion.div variants={fadeUp} className="max-home__hero-actions">
              <a
                href="#contact"
                className="max-btn max-btn--primary"
                onClick={openEnquiry}
              >
                Schedule a conversation
                <ArrowRight size={18} strokeWidth={2} />
              </a>
              <Link to="/register" className="max-btn max-btn--ghost">
                Explore Trial account
              </Link>
            </motion.div>
          </div>
          <motion.aside
            variants={fadeUp}
            className="max-home__hero-side"
            aria-label="Operating commitments"
          >
            <HeroBranchingSystem />
          </motion.aside>
        </div>
      </motion.section>

      {/* PILLARS SECTION */}
      <section
        id="core-pillars"
        className="max-home__section max-home__section--glass"
      >
        <motion.div
          className="max-container"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-50px" }}
          variants={staggerContainer}
        >
          <header className="max-home__section-head">
            <motion.h2 variants={fadeUp} className="max-home__h2">
              Core Pillars of Excellence
            </motion.h2>
            <motion.p variants={fadeUp} className="max-home__sub">
              Digitize and streamline your plant floor operations with an
              interconnected platform designed for high-end manufacturing
              floors.
            </motion.p>
          </header>
          <div className="max-home__cards max-home__cards--6">
            {CORE_PILLARS.map((pillar, idx) => (
              <motion.article
                key={idx}
                variants={fadeUp}
              >
                <SpotlightCard>
                  <div className="max-home__card-icon" aria-hidden="true">
                    <pillar.Icon size={24} strokeWidth={1.5} />
                  </div>
                  <h3 className="max-home__h3">{pillar.label}</h3>
                  <p className="max-home__card-text">{pillar.desc}</p>
                </SpotlightCard>
              </motion.article>
            ))}
          </div>
        </motion.div>
      </section>

      {/* SEGMENTS SECTION */}
      <section
        id="segments"
        className="max-home__section max-home__section--paper"
      >
        <motion.div
          className="max-container"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-50px" }}
          variants={staggerContainer}
        >
          <header className="max-home__section-head">
            <motion.h2 variants={fadeUp} className="max-home__h2">
              Built for every stage of growth
            </motion.h2>
            <motion.p variants={fadeUp} className="max-home__sub">
              Whether you are a focused SME, a fast-scaling MSME, or a
              multi-site enterprise, we align people, process, and technology to
              your reality — not a generic playbook.
            </motion.p>
          </header>
          <div className="max-home__cards">
            <motion.article
              variants={fadeUp}
            >
              <SpotlightCard>
                <div className="max-home__card-icon" aria-hidden="true">
                  <Factory size={22} strokeWidth={1.5} />
                </div>
                <h3 className="max-home__h3">SME</h3>
                <p className="max-home__card-text">
                  Practical, high-impact engagements that prioritize quick wins,
                  clear governance, and sustainable routines on the shop floor.
                </p>
              </SpotlightCard>
            </motion.article>
            <motion.article
              variants={fadeUp}
            >
              <SpotlightCard>
                <div className="max-home__card-icon" aria-hidden="true">
                  <Layers size={22} strokeWidth={1.5} />
                </div>
                <h3 className="max-home__h3">MSME</h3>
                <p className="max-home__card-text">
                  Structured programs that connect growing operations —
                  standardizing workflows while preserving the agility that made
                  you successful.
                </p>
              </SpotlightCard>
            </motion.article>
            <motion.article
              variants={fadeUp}
            >
              <SpotlightCard>
                <div className="max-home__card-icon" aria-hidden="true">
                  <Network size={22} strokeWidth={1.5} />
                </div>
                <h3 className="max-home__h3">Enterprise</h3>
                <p className="max-home__card-text">
                  Portfolio-level alignment across plants, systems, and suppliers
                  — with governance, reporting, and adoption that match enterprise
                  complexity.
                </p>
              </SpotlightCard>
            </motion.article>
          </div>
        </motion.div>
      </section>

      {/* APPROACH SECTION */}
      <section
        id="approach"
        className="max-home__section max-home__section--muted"
      >
        <motion.div
          className="max-container max-home__split"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-50px" }}
          variants={staggerContainer}
        >
          <motion.div variants={fadeUp}>
            <h2 className="max-home__h2">More than software</h2>
            <p className="max-home__sub max-home__sub--left">
              Many vendors sell tools. MaX Consulting delivers outcomes:
              assessment, roadmap, change management, training, and a connected
              SaaS layer that turns intent into measurable performance — OEE,
              safety, maintenance, and beyond.
            </p>
          </motion.div>
          <motion.ul variants={fadeUp} className="max-home__list">
            <li>
              <span className="max-home__list-num">01</span>
              <span>
                Diagnostic and maturity assessment against your manufacturing
                context
              </span>
            </li>
            <li>
              <span className="max-home__list-num">02</span>
              <span>
                Target operating model, KPIs, and phased adoption with your
                teams
              </span>
            </li>
            <li>
              <span className="max-home__list-num">03</span>
              <span>
                MaX platform deployment, integrations, and continuous
                improvement cadence
              </span>
            </li>
          </motion.ul>
        </motion.div>
      </section>

      {/* BAND SECTION */}
      <section className="max-home__band">
        <motion.div
          className="max-container max-home__band-inner"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          variants={staggerContainer}
        >
          <motion.p variants={fadeUp} className="max-home__stat-label">
            Experience that shows in the work
          </motion.p>
          <motion.p variants={fadeUp} className="max-home__stat">
            <span className="max-home__stat-num">
              <StatCounter from={0} to={30} />+
            </span>
            <span className="max-home__stat-text">
              years in manufacturing consulting — field-tested with SMEs, MSMEs,
              and global enterprises.
            </span>
          </motion.p>
        </motion.div>
      </section>

      {/* CTA SECTION */}
      <section className="max-home__cta">
        {/* Arch Sparkles Background */}
        <div className="max-home__cta-bg">
          <div className="max-home__cta-gradient" />
          <div className="max-home__cta-arch" />
        </div>
        <motion.div
          className="max-container max-home__cta-inner"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          variants={fadeUp}
        >
          <h2 className="max-home__cta-title">Tell us about your plant</h2>
          <p className="max-home__cta-copy">
            Share your priorities — we will help you map the right mix of
            consulting and platform for your teams.
          </p>
          <a
            href="#contact"
            className="max-btn max-btn--primary"
            onClick={openEnquiry}
          >
            Reach the MaX team
            <ArrowRight size={18} strokeWidth={2} />
          </a>
        </motion.div>
      </section>
    </div>
  );
};

export default MaxHome;
